import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import * as Sentry from '@sentry/vue'
import {
  loginApi,
  oldSystemLoginApi,
  getCoin,
  logoutApi,
  getCurrentTenantList,
  switchTenant,
  getUserInfo,
  switchTenantMobile
} from '@/apis/user'
import useTracker from '@/hooks/useTracker'
import { getSettlementPopUpApi } from '@/apis/record'
import { getToken, setToken, removeToken } from '@/utils/auth'
import type { LoginParams, OldSystemLoginParams, AuthCoinVO, AuthCoinDataVO } from '@/types/user'
import { createLocalStorage } from '@/utils/cache'
import { isEmptyArray } from '@/utils/is'
import { clearMemory } from '@/utils/login'
import { router } from '@/router'
import { useLoading } from '@/hooks/useLoading'
import { usePermissionStore } from '@/stores/modules/permission'
import { useZzyStore } from '@/stores/modules/zzyStore'
import { setCache, getCache } from '@/utils/cache/local'
import { useCallCenter } from './callCenter'
import globalEnv from '@/configs'
import type { SipInfo } from '@/types/callCenter/callCenter'
import { useSocketMsg } from './socketMsg'
import { useTemCache } from '@/stores/modules/temCache'
import { useJsSIP } from '@/hooks/callCenter/jssip'
import { useDaco } from '@/hooks/callCenter/daco'
import { queryByUserIdApi, assignSwitchApi } from '@/apis/systemManage/staffAssignSwitchConfig'
import { getQueryInfo } from '@/apis/auth'
import { getStaffAssignNumConfigApi } from '@/apis/systemManage/staffAssignNumConfig'
import useRouteCache from '@/hooks/useRouteCache'
import { debounce } from 'lodash-es'
import { useRuleConfig } from '@/views/systemSafeMange'
import { useSecurityIdle } from '@/hooks/useSecurityIdle'
import { removeAllKeyupListeners } from '@/utils'
import { useGlobalStore } from '@/stores/modules/global'
import { useSystem } from './system'
const { removeAllCache } = useRouteCache()
const buildEnv = import.meta.env.VITE_APP_BUILD_ENV
interface UserInfo {
  name: string
  id: string | number
  realName: string
  sipInfo: SipInfo
  [key: string]: any
}
type PartialUserInfo = Partial<UserInfo>

interface BranchCompanyItem {
  name: string
  id: string | number
}
interface StaffDistributeSwitch {
  status: boolean
  id: string
  selfOpenAssignSwitch: number // 是否开启自行派单
}
// 派单数量
interface StaffDistributeNum {
  staffPrivateUpNum: number
  customerNum: number
}

// const isInitSentry = ['prod', 'test', 'production'].includes(import.meta.env.MODE)
const isInitSentry = false
export const useAccountStore = defineStore('useAccount', () => {
  // 记录登录状态
  const hasLogin = ref(false)
  // 账户余额数据
  const authCoin = reactive<AuthCoinVO>({
    data: {}
  })
  const userInfo = ref<PartialUserInfo>({})

  // 需确认签收结算单信息
  const confirmReceiptSettlementInfo = ref<any>(null)
  // 分公司列表
  const branchCompanyList = ref<BranchCompanyItem[]>(getCache('branchCompany') || [])

  // 当前分公司
  const currentCompany = ref<BranchCompanyItem>(getCache('currentCompany') as BranchCompanyItem)
  // 当前员工派单信息
  const staffDistributeSwitchInfo = ref<StaffDistributeSwitch>({} as StaffDistributeSwitch)
  const staffDistributeNumInfo = ref<StaffDistributeNum>({} as StaffDistributeNum)
  const showPassWordDialog = ref(false)
  // 当前员工派单信息
  async function getBranchCompanyList(mobile?: string) {
    return new Promise((resolve, reject) => {
      getCurrentTenantList(mobile)
        .then(res => {
          setBranchCompanyList(res)
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  }
  function setShowPassWordDialog(item: any) {
    showPassWordDialog.value = item
  }

  function setCurrentCompany(item: any) {
    currentCompany.value = item
    setCache('currentCompany', item)
  }

  function setBranchCompanyList(list: BranchCompanyItem[]) {
    branchCompanyList.value = list
    setCache('branchCompany', list)
  }

  // 清除分公司相关缓存
  function clearBranchCompany() {
    setCurrentCompany(null)
    setBranchCompanyList([])
  }

  // 修改登录状态
  function setHasLogin(bool: boolean) {
    hasLogin.value = bool
  }

  // 设置账户余额数据
  function setAuthCoin(authCoinValue: AuthCoinDataVO) {
    authCoin.data = authCoinValue
  }

  // 登录
  function login(data: LoginParams, clearStore = true) {
    const permissionStore = usePermissionStore()
    clearStore && permissionStore.clearCache()
    return new Promise((resolve, reject) => {
      loginApi(data)
        .then(res => {
          console.log(res)
          // 存token
          setToken('Bearer ' + res)
          // getUserInfoAndMenus()
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  }

  // 获取用户信息和菜单
  function getUserInfoAndMenus() {
    const permissionStore = usePermissionStore()
    const zzyStore = useZzyStore()
    const callCenter = useCallCenter()
    const { getConfig } = useRuleConfig()
    return new Promise((resolve, reject) => {
      Promise.allSettled([getUser(), getQuery()])
        .then(async results => {
          if (results[0].status === 'rejected') {
            reject(results[0].reason || false)
            return
          }
          try {
            const socketMsg = useSocketMsg()
            // 连登老系统
            if (buildEnv !== 'erp') {
              // oldSystemLogin({ type: globalEnv.oldSystemType })
              // 检查结算单信息
              checkSettlementPop()
            }
            await permissionStore.getSystemList()
            const res = await getConfig()
            const { oneEnable, outLoginTimeDelay } = res || {}
            const { init } = useSecurityIdle()
            oneEnable && init(outLoginTimeDelay * 60 * 1000)
            // socket
            socketMsg.initSocket()
            // 初始化呼叫中心
            if (buildEnv !== 'erp') {
              callCenter.initCallCenter()
            }
            // 获取是否购买指掌易
            zzyStore.setIsBuyZzy()
            // 获取是否购买融掌通
            zzyStore.setIsBuyMDM()
            resolve('success')
          } catch (e) {
            reject(false)
          }
        })
        .catch(() => {
          reject()
        })
    })
  }

  function getUser() {
    return new Promise((resolve, reject) => {
      getUserInfo()
        .then(res => {
          const {
            id,
            username,
            tenantId,
            tenantName,
            uiVersion,
            isCpsType = false,
            paymentCode = ''
          } = res
          userInfo.value = { ...userInfo.value, ...res, isCpsOrg: isCpsType, paymentCode }
          if (isInitSentry) {
            // 设置sentry信息
            Sentry.setUser({ id, username })
            Sentry.setContext('character', {
              tenantId,
              tenantName,
              curd: btoa(getToken() || ''),
              hash: __GIT_HASH__
            })
          }
          // 初始化布局
          useSystem().initLayout(uiVersion)
          resolve(userInfo.value)
        })
        .catch(err => {
          reject(err)
        })
    })
  }
  function getQuery() {
    getQueryInfo().then(res => {
      // console.log(res, 'getQuery')
      userInfo.value.messageNotifyState = res?.messageNotifyState?.code
    })
  }

  // 老系统连登
  function oldSystemLogin(data: OldSystemLoginParams) {
    return new Promise<void>((resolve, reject) => {
      oldSystemLoginApi(data)
        .then(res => {
          // 存储老系统token
          createLocalStorage().set('oldSystemToken', res)
          resolve(res)
        })
        .catch(err => {
          reject(err)
          createLocalStorage().remove('oldSystemToken')
        })
    })
  }

  // 获取账户余额
  function getAuthCoin() {
    return new Promise<void>((resolve, reject) => {
      getCoin()
        .then(res => {
          // 存储余额
          setAuthCoin(res)
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  }

  // 切换分公司
  async function checkBranchCompany(row: BranchCompanyItem) {
    // const permissionStore = usePermissionStore()
    const socketMsg = useSocketMsg()
    const { openFull, close } = useLoading()
    const mobile = getCache('SwitchMobile')
    openFull()
    let newToken = ''
    try {
      if (mobile) {
        newToken = await switchTenantMobile(row.id, mobile)
      } else {
        newToken = await switchTenant(row.id)
      }
      if (newToken) {
        // 清除缓存（nva、token） 重新登录 重新获取系统菜单
        setCurrentCompany(row)
        socketMsg.closeSocket()
        socketMsg.clearMsgList()
        clearMemory()
        setToken('Bearer ' + newToken)
        // getUserInfoAndMenus()
      }
    } finally {
      close()
    }
  }

  // 登出
  async function logout(noLogout: boolean = false, jump: boolean = true) {
    // const permissionStore = usePermissionStore()
    const socketMsg = useSocketMsg()
    const temCache = useTemCache()
    const callCenter = useCallCenter()
    // 退出登录前，上报页面浏览时长
    const { tracker } = useTracker()
    if (tracker.leaveRouteInfo) {
      tracker.trackPageLeave(tracker.leaveRouteInfo)
    }
    // 退出登录前，断开呼叫中心连接
    await useJsSIP().jssipStop()
    await useDaco().oncloseWS()
    try {
      if (!noLogout) {
        setTimeout(async () => {
          await logoutApi()
        })
      }
    } finally {
      // 清除缓存数据
      clearMemory()
      // 清除分公司数据 切换分公司的时候不能清除
      clearBranchCompany()
      setMobile('')
      // 断开socket
      socketMsg.closeSocket()
      socketMsg.clearMsgList()
      temCache.clearData()
      createLocalStorage().remove('dataThroughFlag')
      localStorage.removeItem('USER_ID')
      callCenter?.reportSeatStatusTimer && clearInterval(callCenter.reportSeatStatusTimer)
      callCenter.setCallAddCustom(false)
      console.log('退出登录')
      // 退出清空keep-alive缓存
      removeAllCache()
      removeToken()
      setShowPassWordDialog(false) // 关闭超管强制修改密码弹窗
      userInfo.value = {}
      // 如果是iframe内嵌，则不跳登录页, 并向父窗口发送消息
      if (useGlobalStore().frameCode) {
        window.parent.postMessage(
          { type: 'logout', data: { frameCode: useGlobalStore().frameCode } },
          '*'
        )
      }
      jump &&
        router.push({
          path: '/login',
          query: getCache('redirect')
        })
      // router.push(`/login?redirect=${router?.currentRoute?.value?.fullPath}`)

      // 客户详情-键盘监听事件(左右切换)标志符(_CUSTOMERDETAIL_PAGE_KEYUP_EVENT_)
      // // @ts-ignore
      // window._CUSTOMERDETAIL_PAGE_KEYUP_EVENT_ = {}
      removeAllKeyupListeners()
    }
  }
  // 登录检查结算单信息
  const checkSettlementPop = () => {
    getSettlementPopUpApi()
      .then(res => {
        if (res && !isEmptyArray(res)) {
          confirmReceiptSettlementInfo.value = res
        }
      })
      .catch(() => {
        confirmReceiptSettlementInfo.value = null
      })
  }
  // 清空结算单信息
  const resetSettlementInfo = () => {
    confirmReceiptSettlementInfo.value = null
  }

  function setMobile(str: string) {
    setCache('SwitchMobile', str)
  }
  // 获取当前登录者派单开关信息
  const getStaffDistributeSwitchInfo = debounce(() => {
    queryByUserIdApi().then(res => {
      staffDistributeSwitchInfo.value = {
        status: !!res?.assignSwitch || false,
        id: res?.id || '',
        selfOpenAssignSwitch: res?.selfOpenAssignSwitch ?? 0
      }
    })
  }, 500)
  // 设置当前登录者派单开关信息
  function setStaffDistributeSwitchInfo({
    idList,
    assignSwitch
  }: {
    idList: string[]
    assignSwitch: boolean
  }) {
    return new Promise((resolve, reject) => {
      assignSwitchApi(assignSwitch ? 1 : 0)
        .then(() => {
          resolve(true)
        })
        .catch(error => reject(error))
    })
  }
  // 获取当前登录者的派单数量信息
  function getStaffDistributeNumInfo() {
    return new Promise(resolve => {
      getStaffAssignNumConfigApi()
        .then(res => {
          staffDistributeNumInfo.value = res as StaffDistributeNum
          resolve(true)
        })
        .catch(() => {
          resolve(false)
        })
    })
  }
  // 设置当前登录者的派单数量信息
  function setStaffDistributeNumInfo(data: StaffDistributeNum) {
    staffDistributeNumInfo.value = Object.assign(staffDistributeNumInfo.value, data)
  }
  return {
    hasLogin,
    authCoin,
    currentCompany,
    userInfo,
    confirmReceiptSettlementInfo,
    login,
    oldSystemLogin,
    getUser,
    setHasLogin,
    getAuthCoin,
    setAuthCoin,
    logout,
    branchCompanyList,
    checkBranchCompany,
    getBranchCompanyList,
    setBranchCompanyList,
    checkSettlementPop,
    resetSettlementInfo,
    getUserInfoAndMenus,
    setMobile,
    staffDistributeSwitchInfo,
    getStaffDistributeSwitchInfo,
    setStaffDistributeSwitchInfo,
    staffDistributeNumInfo,
    getStaffDistributeNumInfo,
    setStaffDistributeNumInfo,
    setShowPassWordDialog,
    showPassWordDialog
  }
})
